import React from "react";
import { useTranslation } from "react-i18next";
import { TwFormHelperText } from "../../../components/tailwind";
import { commissionType, sellerTypes } from "../../../utils/constants";

/**
 * Event → Sales channel tab.
 *
 * READ-ONLY in the campaign-first model: every seller/channel action
 * (add, invite, commission, banner, disable, payouts, performance) now lives in
 * **My Promotions**, which is the single source of truth. This tab just lists
 * who is selling this event across all campaigns — including any legacy channel
 * with no campaign, which My Promotions (campaign-scoped) wouldn't surface.
 *
 * The prop signature is preserved so EventDetails.js keeps working unchanged;
 * the write-path props (formValues/handleUpdate/…) are simply no longer used.
 */
export default function SalesChannleTab({
  eventScheduleData,
  formValues,
  setFormValues,
  setTabCurrentState,
  edit = false,
  handleUpdate,
  isFromDetail = false,
}) {
  const { t } = useTranslation();

  const rows = (eventScheduleData?.salesChannelList || []).filter((f) => f.isDeleted === false);

  return (
    <div className={`flex flex-col gap-4 ${isFromDetail === false ? "sm:px-8" : ""}`}>
      <div className={`flex flex-col gap-4 ${edit === false ? "border-2 border-bright-gray rounded-2xl mt-5" : ""}`}>
        <div className="flex gap-2 items-center w-full">
          <div className={`items-left w-full ${isFromDetail === false ? "text-base sm:text-2xl" : "text-2xl sm:text-3xl"}`}>
            {t("myEvents.eventSalesChannel")}
          </div>
          <div className="flex justify-end w-full">
            {/* All seller management is in My Promotions (campaign-first). */}
            <span className="text-xs text-gray-400 italic">
              {t("myEvents.manageInPromotions") || "Manage sellers from My Promotions"}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle sm:px-1 overflow-x-auto">
            <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-form">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      {t("myEvents.sellerName")}
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t("myEvents.commissionType")}
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t("myEvents.channelType")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {rows.length > 0 ? (
                    rows.map((row, i) => (
                      <tr key={i}>
                        <td className="whitespace-nowrap py-5 pt-6 pl-4 pr-3 text-sm font-bold text-gray-900 sm:pl-6">
                          <div>
                            {row.name}
                            <p className="text-sm text-gray-400">
                              {t("myEvents.ref")} {row.sellerReferenceId}
                            </p>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold capitalize">
                          {row.isCommissionApplicable === true ? (
                            <div>
                              {t("myEvents." + row.commissionModelType)}
                              <p className="text-sm text-gray-400">{commissionType[row.unitType]}</p>
                            </div>
                          ) : (
                            t("myEvents.noCommission")
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold capitalize">
                          <div>
                            {sellerTypes.find((f) => f.value === row.channeltype)?.title || row.channeltype || "-"}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 text-center">
                        {t("myEvents.noSalesChannelCreated")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
